import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { IconCheckCircle, IconError, IconArrowRight } from "suomifi-icons";
import type { Kayttaja, KayttajaTapahtuma, IdProofingVaihe } from "../types";
import { formatAikaleima } from "../utils/format";
import { ID_PROOFING_MAX } from "../utils/oikeudet";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useGetKayttajaTiedostoQuery } from "../api/backendApi";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect, useState } from "react";

interface Props {
  kayttaja: Kayttaja;
  /** Jos true, näytetään kaikki yritykset (esim. erillisellä sivulla).
   * Default false → vain viimeisin + linkki "näytä kaikki". */
  naytaKaikki?: boolean;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.l};
`;

const Yritys = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};

  & + & {
    padding-top: ${({ theme }) => theme.spacing.l};
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.s};
`;

interface TagProps {
  $bg: string;
  $fg: string;
}

const Tag = styled.span<TagProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  white-space: nowrap;
  flex-shrink: 0;

  & > svg {
    width: 0.9375rem;
    height: 0.9375rem;
  }
`;

const Aika = styled.span`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  flex: 1;
  text-align: right;
`;

const Meta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.m};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  flex-wrap: wrap;
`;

const MetaKey = styled.span`
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const MetaValue = styled.span<{ $alert?: boolean }>`
  color: ${({ theme, $alert }) =>
    $alert ? theme.colors.alertBase : theme.colors.blackBase};
`;

/* Vaakavaiheket — ID-proofing 4-vaihetta visuaalisena polkuna.
   Onnistunut → katkoviiva ennen "ei päästy" -vaihetta. */
const Vaiheket = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Erotin = styled.span<{ $reached: boolean }>`
  display: inline-block;
  width: 1rem;
  height: 1px;
  background: ${({ theme }) => theme.colors.depthDark1};
  border-top: ${({ $reached }) => ($reached ? "none" : "1px dashed")};
  border-color: ${({ theme }) => theme.colors.depthBase};
  ${({ $reached, theme }) =>
    !$reached &&
    `background: transparent; border-bottom: 1px dashed ${theme.colors.depthBase};`}
`;

/* Kuvavertailu — kaksi mock-kuvaa vierekkäin. */
const Kuvavertailu = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const KuvavertailuOtsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const KuvaPari = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.m};
  max-width: 32rem;
`;

const KuvaSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const KuvaContainer = styled.div`
  aspect-ratio: 3 / 4;
  background: ${({ theme }) => theme.colors.depthLight3};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  overflow: hidden;
  position: relative;
`;

const KuvaImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const KuvaLabel = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.blackBase};
  text-align: center;
`;

const VastaavuusRivi = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.s};
`;

/* MRZ-vaiheessa epäonnistuneessa yrityksessä näytetään yksittäinen kuva,
   jonka käyttäjä on yrittänyt ottaa asiakirjasta. Sama KuvaContainer/
   KuvaImg-asettelu kuin selfievertailussa, mutta max-width pitää kuvan
   suunnilleen saman kokoisena kuin yksi KuvaPari-sarake. */
const MrzKuvaWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  max-width: 15rem;
`;

const ShowAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  text-decoration: none;
  align-self: flex-start;

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.highlightDark1};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }
`;

const KuvaImgContain = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain; /* ← show whole image, no crop */
  display: block;
`;

const Empty = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

/* ID-proofing-lukituksen tarkka info — sijoitetaan kortin ylälaitaan
   ylimmäksi laatikoksi kun käyttäjä on lukittu. Sivupalkin LukittuTag
   on saman tiedon kompakti badge-versio. */
const LukitusBox = styled.div`
  background: ${({ theme }) => theme.colors.alertLight1};
  border-left: 4px solid ${({ theme }) => theme.colors.alertBase};
  padding: ${({ theme }) => theme.spacing.s};
  margin-bottom: ${({ theme }) => theme.spacing.s};
`;

const LukitusOtsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.alertBase};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const LukitusTeksti = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const LukitusSubText = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

function formatCountdown(iso: string, t: TFunction): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return t("tekstit.vapautuu_pian");
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return t("tekstit.countdown", { h, m });
}

type VaiheStatus = "ok" | "fail" | "skipped";

const VAIHEET: { vaihe: IdProofingVaihe; labelKey: string }[] = [
  { vaihe: "sahkoposti", labelKey: "vaiheet.sahkoposti" },
  { vaihe: "mrz", labelKey: "vaiheet.mrz" },
  { vaihe: "siru", labelKey: "vaiheet.siru" },
  { vaihe: "kuva", labelKey: "vaiheet.kuva" },
  { vaihe: "tarkistus", labelKey: "vaiheet.tarkistus" },
];

/** Päättele kunkin vaiheen status yritykselle. */
function vaiheStatukset(
  yritys: KayttajaTapahtuma,
  sahkopostiVerifioitu: boolean,
): Record<IdProofingVaihe, VaiheStatus> {
  // Sähköposti — käyttäjäkohtainen (vahvistettu rekisteröinnin yhteydessä)
  const sahkopostiOk = sahkopostiVerifioitu;

  if (yritys.onnistui === true) {
    // Onnistunut yritys — kaikki vaiheet ok
    return {
      sahkoposti: sahkopostiOk ? "ok" : "fail",
      mrz: "ok",
      siru: "ok",
      kuva: "ok",
      tarkistus: "ok",
    };
  }

  // Epäonnistunut — kaatunut vaihe = fail, sitä ennen ok, sen jälkeen skipped
  const order: IdProofingVaihe[] = [
    "sahkoposti",
    "mrz",
    "siru",
    "kuva",
    "tarkistus",
  ];
  const failedIdx = yritys.epaonnistunutVaihe
    ? order.indexOf(yritys.epaonnistunutVaihe)
    : -1;

  return {
    sahkoposti: failedIdx === 0 ? "fail" : sahkopostiOk ? "ok" : "fail",
    mrz:
      failedIdx === 1
        ? "fail"
        : failedIdx > 1 || failedIdx === -1
        ? "ok"
        : "skipped",
    siru:
      failedIdx === 2
        ? "fail"
        : failedIdx > 2 || failedIdx === -1
        ? "ok"
        : "skipped",
    kuva:
      failedIdx === 3
        ? "fail"
        : failedIdx > 3 || failedIdx === -1
        ? "ok"
        : "skipped",

    tarkistus: failedIdx === 4 ? "fail" : failedIdx === -1 ? "ok" : "skipped",
  };
}
const BACKEND = import.meta.env.VITE_BACKEND_ADDRESS;
function kuvaSrc(
  kayttaja: Kayttaja,
  filename: string | undefined,
): string | undefined {
  const nimi = filename?.trim();
  if (!nimi) {
    return undefined; // ← Changed from null
  }
  return `${BACKEND}/api/users/${kayttaja.uid}/tiedostot/${encodeURIComponent(
    nimi,
  )}`;
}

function YritysRivi({
  yritys,
  kayttaja,
}: {
  yritys: KayttajaTapahtuma;
  kayttaja: Kayttaja;
}) {
  const { t } = useTranslation(["todentaminen", "common"]);
  const theme = useTheme();
  // Fetch asiakirjaKuva
  const [asiakirjaKuva, setAsiakirjaKuva] = useState<string | undefined>();
  const [vertailuAsiakirjaKuva, setVertailuAsiakirjaKuva] = useState<
    string | undefined
  >();
  const [vertailuSelfieKuva, setVertailuSelfieKuva] = useState<
    string | undefined
  >();

  useEffect(() => {
    async function fetchImage(
      filename: string | undefined,
      setSrc: (url: string) => void,
    ) {
      if (!filename) return;
      try {
        const response = await fetch(
          `/api/users/${kayttaja.uid}/tiedostot/${encodeURIComponent(
            filename,
          )}`,
        );
        if (!response.ok) throw new Error("Failed to fetch image");
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setSrc(url);
        return () => URL.revokeObjectURL(url); // cleanup
      } catch (error) {
        console.error(`Failed to fetch image ${filename}:`, error);
      }
    }

    fetchImage(yritys.kuvat?.asiakirjaKuvanNimi, setAsiakirjaKuva);
    fetchImage(
      yritys.kuvat?.vertailuAsiakirjaKuvanNimi,
      setVertailuAsiakirjaKuva,
    );
    fetchImage(yritys.kuvat?.vertailuSelfieKuvanNimi, setVertailuSelfieKuva);
  }, [kayttaja.uid, yritys.kuvat]);

  const success = yritys.onnistui === true;

  const statukset = vaiheStatukset(yritys, kayttaja.sahkopostiVerifioitu);

  // Kuvavertailu näytetään vain jos vaihe 4 ehdittiin tai onnistui
  const naytaKuvavertailu = success || yritys.epaonnistunutVaihe === "kuva";

  // MRZ- tai siru-vaiheessa epäonnistunut: näytetään käyttäjän asiakirjasta
  // ottama kuva. MRZ-vaiheessa kuva selittää suoraan luvun epäonnistumista;
  // siruvaiheessa MRZ on jo onnistunut ja puhelin on ehtinyt ottaa kuvan,
  // jonka perusteella virkailija voi arvioida mahdolliset NFC-luvun esteet
  // (esim. taittunut sivu, refleksit, väärä sivu auki).
  const naytaAsiakirjakuva =
    yritys.epaonnistunutVaihe === "mrz" || yritys.epaonnistunutVaihe === "siru";

  function colorFor(status: VaiheStatus | "pending"): {
    $bg: string;
    $fg: string;
  } {
    if (status === "pending") {
      return {
        $bg: theme.colors.depthLight2,
        $fg: theme.colors.depthDark1,
      };
    }
    if (status === "ok")
      return {
        $bg: theme.colors.successSecondary,
        $fg: theme.colors.successDark1,
      };
    if (status === "fail")
      return {
        $bg: theme.colors.alertLight1,
        $fg: theme.colors.alertBase,
      };
    return {
      $bg: theme.colors.depthLight2,
      $fg: theme.colors.depthDark1,
    };
  }

  function symbolFor(status: VaiheStatus | "pending"): string {
    if (status === "pending") return "◐";
    if (status === "ok") return "✓";
    if (status === "fail") return "✗";
    return "○";
  }
  const pending = yritys.tyyppi === "id_proofing_aloitettu";
  return (
    <Yritys>
      <StatusRow>
        {pending ? (
          // ✅ Gray status for pending/started
          <Tag $bg={theme.colors.depthLight2} $fg={theme.colors.depthDark1}>
            <span aria-hidden>◐</span>
            {t("todentaminen.aloitettu")}
          </Tag>
        ) : success ? (
          <Tag
            $bg={theme.colors.successSecondary}
            $fg={theme.colors.successDark1}
          >
            <IconCheckCircle aria-hidden />
            {t("todentaminen.todennettu")}
          </Tag>
        ) : (
          <Tag $bg={theme.colors.alertLight1} $fg={theme.colors.alertBase}>
            <IconError aria-hidden />
            {t("todentaminen.epaonnistui")}
          </Tag>
        )}
        <Aika>{formatAikaleima(yritys.aika)}</Aika>
      </StatusRow>

      <Meta>
        {yritys.konteksti && (
          <span>
            <MetaKey>{t("meta.syy")}: </MetaKey>
            <MetaValue $alert={!success}>
              {t(`common:tukipyynto.aihe.${yritys.konteksti}`)}
            </MetaValue>
          </span>
        )}
      </Meta>

      <Vaiheket>
        {VAIHEET.map((v, idx) => {
          // ✅ If pending, all vaiheet are pending
          const vaiheStatus: VaiheStatus | "pending" = pending
            ? "pending"
            : statukset[v.vaihe];

          const nextStatus =
            idx < VAIHEET.length - 1
              ? pending
                ? "pending"
                : statukset[VAIHEET[idx + 1].vaihe]
              : null;
          const reachedNext =
            nextStatus !== null &&
            nextStatus !== "skipped" &&
            nextStatus !== "pending";

          return (
            <span
              key={v.vaihe}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <Tag {...colorFor(vaiheStatus)}>
                <span aria-hidden>{symbolFor(vaiheStatus)}</span>
                {t(v.labelKey)}
              </Tag>
              {idx < VAIHEET.length - 1 && (
                <Erotin $reached={reachedNext} aria-hidden />
              )}
            </span>
          );
        })}
      </Vaiheket>
      {naytaAsiakirjakuva && asiakirjaKuva && (
        <Kuvavertailu>
          <KuvavertailuOtsikko>
            {t("otsikot.kayttajan_kuva_asiakirjasta")}
          </KuvavertailuOtsikko>
          <MrzKuvaWrap>
            <KuvaContainer>
              <KuvaImgContain
                src={asiakirjaKuva}
                alt={t("labels.asiakirjakuva")}
              />
            </KuvaContainer>
            <KuvaLabel>{t("labels.asiakirjakuva")}</KuvaLabel>
          </MrzKuvaWrap>
        </Kuvavertailu>
      )}

      {naytaKuvavertailu && (vertailuAsiakirjaKuva || vertailuSelfieKuva) && (
        <Kuvavertailu>
          <KuvavertailuOtsikko>{t("otsikot.kuvavertailu")}</KuvavertailuOtsikko>
          <KuvaPari>
            {vertailuAsiakirjaKuva && (
              <KuvaSlot>
                <KuvaContainer>
                  <KuvaImg
                    src={vertailuAsiakirjaKuva}
                    alt={t("labels.passikuva")}
                  />
                </KuvaContainer>
                <KuvaLabel>{t("labels.passikuva")}</KuvaLabel>
              </KuvaSlot>
            )}
            {vertailuSelfieKuva && (
              <KuvaSlot>
                <KuvaContainer>
                  <KuvaImg src={vertailuSelfieKuva} alt={t("labels.selfie")} />
                </KuvaContainer>
                <KuvaLabel>Selfie</KuvaLabel>
              </KuvaSlot>
            )}
          </KuvaPari>
          {yritys.kuvavertailuProsentti !== undefined && (
            <VastaavuusRivi>{/* ... existing code ... */}</VastaavuusRivi>
          )}
        </Kuvavertailu>
      )}
    </Yritys>
  );
}

const ManuaaliprosessiTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.highlightLight2};
  color: ${({ theme }) => theme.colors.highlightDark1};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
  align-self: flex-start;
`;

export default function HenkilollisyydenTodentaminen({
  kayttaja,
  naytaKaikki = false,
}: Props) {
  // Suodata vain ID-proofing-tapahtumat ja lajittele uusin ylhäällä.

  const { t } = useTranslation(["todentaminen", "common"]);
  const yritykset = kayttaja.tapahtumat
    .filter(
      (t) =>
        t?.tyyppi &&
        [
          "id_proofing_onnistui",
          "id_proofing_epaonnistui",
          "id_proofing_aloitettu",
          "asiakirjan_paivitys",
        ].includes(t.tyyppi),
    )
    .sort((a, b) => {
      const dateA = new Date(a.aika ?? 0).getTime();
      const dateB = new Date(b.aika ?? 0).getTime();
      return dateB - dateA;
    });
  const manuaaliprosessi = kayttaja.manuaaliprosessillaLuotu === true;

  if (yritykset.length === 0 && !manuaaliprosessi && !kayttaja.lukittu) {
    return <Empty>{t("ei.yrityksia")}</Empty>;
  }

  const naytettavat = naytaKaikki ? yritykset : yritykset.slice(0, 1);

  return (
    <Wrap>
      {kayttaja.lukittu && (
        <LukitusBox>
          <LukitusOtsikko>{t("otsikot.proofing_lukittu")}</LukitusOtsikko>
          <LukitusTeksti>
            {kayttaja.idProofingYritykset} {t("tekstit.yrityksia")}
          </LukitusTeksti>
          {kayttaja.lukitusVapautuu && (
            <LukitusSubText>
              {t("tekstit.vapautuu")}:{" "}
              {formatCountdown(kayttaja.lukitusVapautuu, t)}
            </LukitusSubText>
          )}
        </LukitusBox>
      )}
      {manuaaliprosessi && (
        <ManuaaliprosessiTag>{t("tagit.manuaaliprosessi")}</ManuaaliprosessiTag>
      )}
      {naytettavat.map((y) => (
        <YritysRivi key={y.id} yritys={y} kayttaja={kayttaja} />
      ))}
      {!naytaKaikki && yritykset.length > 1 && (
        <ShowAllLink to={`/kayttajat/${kayttaja.uid}/todennusyritykset`}>
          {t("linkit.nayta")} ({yritykset.length})
          <IconArrowRight />
        </ShowAllLink>
      )}
    </Wrap>
  );
}
